<template>
  <div class="min-h-screen">
    <!-- Processing Overlay -->
    <v-overlay :value="!!processingReport" z-index="9999" opacity="0.7">
      <div class="flex flex-col items-center justify-center text-center">
        <v-progress-circular
          indeterminate
          size="64"
          width="6"
          color="primary"
          class="mb-4"
        ></v-progress-circular>
        <h3
          class="text-white font-semibold text-lg bg-black/60 px-6 py-3 rounded-lg backdrop-blur-sm shadow-xl border border-white/20"
        >
          Generating {{ processingReport }}...
        </h3>
      </div>
    </v-overlay>

    <ActitiveTabcomponent
      :tabs="reportTabs"
      :defaultTab="'download'"
      @tab-changed="handleTabChange"
    >
      <!-- Tab Buttons Slot -->
      <template #tab-buttons="{ activeTab }">
        <v-btn
          v-if="activeTab === 'schedule'"
          color="primary"
          @click="addDialog = true"
          class="text-white"
        >
          <v-icon left>mdi-plus</v-icon>
          Add
        </v-btn>
      </template>

      <!-- Main Content -->
      <template #download>
        <div class="">
          <!-- Loan Management Section -->
          <div class="mb-2" v-if="sectionVisible('loan_management')">
            <h2 class="text-lg font-medium text-gray-900 mb-2 font-semibold">Loan Management</h2>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <v-card
                flat
                class="cursor-pointer hover:bg-gray-50 border border-gray-200 rounded-lg transition-all duration-200"
                @click="openReportDialog('Loan Collection Report', 'loan_management')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-blue-50 p-3 rounded-lg border border-blue-100">
                      <v-icon color="blue darken-4" size="24">mdi-cash-multiple</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-bold text-gray-800 mb-2 text-base">Loan Collection Report</h3>
                      <p class="text-sm text-gray-600 leading-relaxed font-medium">
                        Track and analyze total loan collections and performance trends across
                        regions.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                flat
                class="cursor-pointer hover:bg-gray-50 border border-gray-200 rounded-lg transition-all duration-200"
                @click="openReportDialog('Daily Collection Status Report', 'loan_management')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-blue-50 p-3 rounded-lg border border-blue-100">
                      <v-icon color="blue darken-4" size="24">mdi-calendar-check</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-bold text-gray-800 mb-2 text-base">
                        Daily Collection Status
                      </h3>
                      <p class="text-sm text-gray-600 leading-relaxed font-medium">
                        Monitor day-by-day collection status and agent productivity in real-time.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('EMI DUE Report', 'loan_management')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-blue-100 p-3 rounded-lg">
                      <v-icon color="blue darken-2" size="24">mdi-clock-alert</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">EMI DUE Report</h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        View upcoming and pending EMI payments for immediate follow-up.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Detailed Due Report', 'loan_management')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-blue-100 p-3 rounded-lg">
                      <v-icon color="blue darken-2" size="24">mdi-file-document-outline</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">
                        Detailed Due Report
                      </h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Comprehensive summary of all pending dues with detailed aging analysis.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Non Starter Report', 'loan_management')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-blue-100 p-3 rounded-lg">
                      <v-icon color="blue darken-2" size="24">mdi-account-plus-outline</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">Non Starter Report</h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Track accounts where the first installment has not yet been collected.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Loan Expiry Report', 'loan_management')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-blue-100 p-3 rounded-lg">
                      <v-icon color="blue darken-2" size="24">mdi-file-alert-outline</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">Loan Expiry Report</h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Identify loans that have passed their tenure but still have pending dues.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('VAS Report', 'loan_management')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-blue-100 p-3 rounded-lg">
                      <v-icon color="blue darken-2" size="24">mdi-currency-usd-off</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">VAS Report</h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Report on Value Added Services charges including bounce and late fees.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Days Past Due Report', 'loan_management')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-blue-100 p-3 rounded-lg">
                      <v-icon color="blue darken-2" size="24">mdi-calendar-alert</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">
                        Days Past Due Report
                      </h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Track accounts by their DPD status, including cumulative overdue and source
                        details.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Overdue List Report', 'loan_management')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-blue-100 p-3 rounded-lg">
                      <v-icon color="blue darken-2" size="24">mdi-clipboard-list-outline</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">
                        Overdue List Report
                      </h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Complete list of overdue accounts with pending EMI dates, counts, and
                        amounts.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('LCC Report', 'financial')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-indigo-100 p-3 rounded-lg">
                      <v-icon color="indigo darken-2" size="24">mdi-file-chart</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">LCC Report</h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Comprehensive landscape of loan lifecycle including demand, collection, and
                        VAS.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </div>

          <!-- Financial Reports Section -->
          <div class="mb-6" v-if="sectionVisible('loan_management')">
            <h2 class="text-lg font-medium text-gray-900 mb-2 font-semibold">Financial Reports</h2>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Loan PAR Report', 'financial')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-indigo-100 p-3 rounded-lg">
                      <v-icon color="indigo darken-2" size="24">mdi-finance</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">Loan PAR Report</h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Analyze Portfolio at Risk based on various delinquency buckets.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('EMI Tracker Report', 'financial')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-teal-100 p-3 rounded-lg">
                      <v-icon color="teal darken-2" size="24">mdi-cash-sync</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">EMI Tracker Report</h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        End-to-end tracking of all installments from generation to payment.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Cash Inflow Report', 'financial')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-green-100 p-3 rounded-lg">
                      <v-icon color="green darken-2" size="24">mdi-bank-transfer-in</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">Cash Inflow Report</h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Summary of all recoveries including principal, interest, and charges.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Cash Outflow Report', 'financial')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-red-100 p-3 rounded-lg">
                      <v-icon color="red darken-2" size="24">mdi-bank-transfer-out</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">
                        Cash Outflow Report
                      </h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Track disbursed amounts, processing fees, and net outflow.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Business Done Report', 'financial')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-orange-100 p-3 rounded-lg">
                      <v-icon color="orange darken-2" size="24">mdi-briefcase-check</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">
                        Business Done Report
                      </h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Total monthly business sanctioned and overall growth trends.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Loan Closure Report', 'financial')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-blue-grey-100 p-3 rounded-lg">
                      <v-icon color="blue-grey darken-2" size="24">mdi-check-decagram</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">
                        Loan Closure Report
                      </h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Track fully repaid accounts and finalized loan agreements.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Trial Balance Report', 'financial')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-amber-100 p-3 rounded-lg">
                      <v-icon color="amber darken-3" size="24">mdi-scale-balance</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">
                        Trial Balance Report
                      </h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Summary of all ledger balances grouped by categories for period
                        verification.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Profit and Loss Report', 'financial')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-emerald-100 p-3 rounded-lg">
                      <v-icon color="emerald darken-3" size="24">mdi-finance</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">
                        Profit and Loss Report
                      </h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Comprehensive P&L statement combining loan revenues and accounting ledgers.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Balance Sheet Report', 'financial')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-light-blue-100 p-3 rounded-lg">
                      <v-icon color="light-blue darken-3" size="24">mdi-bank-outline</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">
                        Balance Sheet Report
                      </h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Snapshot of company's financial position including Assets, Liabilities, and
                        Equity.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </div>

          <!-- Compliance Reports Section -->
          <div class="mb-6" v-if="sectionVisible('loan_management')">
            <h2 class="text-lg font-medium text-gray-900 mb-2 font-semibold">Compliance Reports</h2>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('CIBIL TUDF Format', 'compliance')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-blue-grey-100 p-3 rounded-lg">
                      <v-icon color="blue-grey darken-2" size="24">mdi-shield-check</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">CIBIL TUDF Format</h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Standard TransUnion Data Format for automated bureau ingestion.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('CIBIL UCRF Format', 'compliance')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-indigo-100 p-3 rounded-lg">
                      <v-icon color="indigo darken-2" size="24">mdi-shield-account</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">CIBIL UCRF Format</h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Legacy/Simplified Uniform Credit Reporting Format (Pipe Delimited).
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </div>

          <!-- Workforce & Attendance Reports Section -->
          <div class="mb-6" v-if="sectionVisible('attendance_management')">
            <h2 class="text-lg font-medium text-gray-900 mb-2 font-semibold">
              Workforce & Attendance
            </h2>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Attendance Report', 'workforce')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-blue-100 p-3 rounded-lg">
                      <v-icon color="blue darken-2" size="24">mdi-calendar-check</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">Attendance Report</h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Detailed attendance matrix with clock-in/out times and locations.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Muster Report-I', 'workforce')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-blue-100 p-3 rounded-lg">
                      <v-icon color="blue darken-2" size="24">mdi-file-table-outline</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">
                        Muster Report-I (DEPRECATED)
                      </h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Legacy formatted attendance report with all employee details.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Muster Report-II', 'workforce')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-blue-100 p-3 rounded-lg">
                      <v-icon color="blue darken-2" size="24">mdi-file-chart-outline</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">Muster Report-II</h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Time-series based attendance and activity summary report.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </div>

          <!-- Collection Management Section -->
          <div class="mb-6" v-if="sectionVisible('collection_management')">
            <h2 class="text-lg font-medium text-gray-900 mb-2 font-semibold">
              Collection Management
            </h2>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Collection Report', 'collection')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-orange-50 p-3 rounded-lg border border-orange-100">
                      <v-icon color="orange darken-4" size="24">mdi-folder-account</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-bold text-gray-800 mb-2 text-base">Collection Report</h3>
                      <p class="text-sm text-gray-600 leading-relaxed font-medium">
                        Overview of all collection cases with status and assignment details.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Visit Report', 'collection')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-orange-50 p-3 rounded-lg border border-orange-100">
                      <v-icon color="orange darken-4" size="24">mdi-map-marker-distance</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-bold text-gray-800 mb-2 text-base">Visit Report</h3>
                      <p class="text-sm text-gray-600 leading-relaxed font-medium">
                        Detailed log of all field visits, outcomes and timestamps.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </div>
        </div>
      </template>
      <template #schedule>
        <div class="pt-2">
          <v-data-table
            :headers="reportHeaders"
            disable-sort
            :items="reportItems"
            flat
            class="elevation-0 border border-gray-200 rounded-lg overflow-hidden"
          >
            <template v-for="(header, index) in reportHeaders" v-slot:[`header.${header.value}`]>
              <p
                :key="'long-' + index"
                class="font-title"
                v-if="header.text?.length > 15"
                v-tooltip="{ text: header.text }"
              >
                {{ header.text }}
              </p>
              <p :key="'short-' + index" v-else class="font-title">{{ header.text }}</p>
            </template>
            <template v-slot:item.name="{ item }">
              <div class="pl-4 text-sm text-gray-800">
                {{ item.name }}
              </div>
            </template>
            <template v-slot:item.type="{ item }">
              <v-chip small color="blue lighten-3" text-color="blue darken-4">
                {{ item.type }}
              </v-chip>
            </template>
            <template v-slot:item.time="{ item }">
              <div class="text-sm pl-4 text-gray-600">
                {{ item.time }}
              </div>
            </template>
            <template v-slot:item.status="{ item }">
              <!-- <v-chip small :color="item.status === 'Active' ? 'green' : 'red'" text-color="white">
              </v-chip> -->
              <div
                :style="{ color: item.status === 'Active' ? '#4CAF50' : '#F44336' }"
                class="font-weight-bold pl-4"
              >
                {{ item.status }}
              </div>
            </template>
            <template v-slot:item.actions="{ item }">
              <div class="flex pl-6">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small v-bind="attrs" v-on="on" @click="deleteReport(item)">
                      <v-icon small color="red">mdi-delete</v-icon>
                    </v-btn>
                  </template>
                  <span>Delete</span>
                </v-tooltip>
              </div>
            </template>
            <v-col
              v-for="(title, index) in reportOptions"
              :key="index"
              cols="12"
              sm="6"
              md="4"
              class="report-card-container"
            >
              <v-card
                class="rounded-xl border-2 border-gray-100 hover:border-primary transition-all duration-300"
                flat
                @click="openReportDialog(title, 'loan_management')"
              >
                <v-card-title class="flex items-center pb-2">
                  <div class="p-3 rounded-lg bg-blue-50 mr-3">
                    <v-icon color="primary">{{ getReportIcon(title) }}</v-icon>
                  </div>
                  <span class="text-lg font-bold text-gray-800">{{ title }}</span>
                </v-card-title>
                <v-card-text class="pt-2">
                  <p class="text-sm text-gray-500 mb-4 h-10">
                    Generate detailed summary and analytics for {{ title.toLowerCase() }}.
                  </p>
                  <div class="flex items-center justify-between">
                    <v-chip
                      x-small
                      color="blue-grey lighten-5"
                      text-color="blue-grey text-darken-2"
                      class="font-bold px-3"
                    >
                      Loan Management
                    </v-chip>
                    <v-btn text color="primary" class="font-bold normal-case px-0">
                      Generate
                      <v-icon right small>mdi-arrow-right</v-icon>
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-data-table>
          <v-dialog v-model="addDialog" max-width="800px" persistent>
            <v-card>
              <!-- Add your dialog content here -->
              <v-card-title class="headline d-flex justify-space-between align-center">
                <div class="d-flex align-center">
                  <v-icon color="primary" class="mr-2">mdi-calendar-clock</v-icon>
                  <h3 class="form-title">Schedule Reports</h3>
                </div>
                <v-btn icon @click="addDialog = false">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text>
                <v-form>
                  <v-text-field
                    label="Report Name"
                    hint="Add custom name for better identification"
                    persistent-hint
                    outlined
                    v-model="form.reportName"
                    class="mb-4"
                    :rules="[(v) => !!v || 'Report name is required']"
                    dense
                    @focus="$event.target.select()"
                  ></v-text-field>

                  <v-select
                    label="Select Reports"
                    :items="reportOptions"
                    v-model="form.selectedReport"
                    outlined
                    class="mb-4"
                    :rules="[(v) => !!v || 'Please select a report']"
                    dense
                    @click:append="form.selectedReport = ''"
                  ></v-select>

                  <v-select
                    label="Select When to Send"
                    :items="scheduleOptions"
                    v-model="form.scheduleTime"
                    outlined
                    class="mb-4"
                    :rules="[(v) => !!v || 'Schedule time is required']"
                    dense
                    @click:append="form.scheduleTime = ''"
                  ></v-select>

                  <v-select
                    label="Select Whose Data to Send"
                    :items="['All Users', 'specific Users', 'Selected Teams']"
                    v-model="form.dataOwner"
                    outlined
                    class="mb-4"
                    :rules="[(v) => !!v || 'Data owner selection is required']"
                    dense
                    @click:append="form.dataOwner = ''"
                  >
                  </v-select>
                  <EmployeeReportdialog
                    v-if="form.dataOwner === 'specific Users'"
                    v-model="isEmployeeReportDialogOpen"
                  />
                </v-form>
              </v-card-text>

              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="primary" dark @click="saveChanges"> Save Changes </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </div>
      </template>
    </ActitiveTabcomponent>

    <GenericExportColumns
      ref="exportDialog"
      :showDialog.sync="showExportDialog"
      :exportKeys="currentExportKeys"
      :categories="currentExportCategories"
      :dialogTitle="'Export ' + selectedReport.title"
      :identifier="selectedReport.title"
      :lockSelection="
        (selectedReport.title && selectedReport.title.includes('CIBIL')) ||
        [
          'Trial Balance Report',
          'Profit and Loss Report',
          'Balance Sheet Report',
          'Vertical Balance Sheet Report'
        ].includes(selectedReport.title)
      "
      showPreviewButton
      :previewRows="previewRows"
      :previewHeaders="previewHeaders"
      :previewHtml="previewHtml"
      :loadingPreview="!!processingReport"
      @exportData="handleExportData"
      @previewData="handlePreviewData"
      @closeExportDialog="showExportDialog = false"
    >
      <template #additional-filters>
        <!-- filters same as before -->
        <div class="px-2 py-4">
          <v-row dense>
            <!-- Date Filter -->
            <v-col cols="12" md="4">
              <v-menu
                v-model="dateMenu"
                :close-on-content-click="false"
                transition="slide-y-transition"
                offset-y
                min-width="auto"
                nudge-bottom="8"
              >
                <template #activator="{ on, attrs }">
                  <div
                    v-bind="attrs"
                    v-on="on"
                    class="flex items-center justify-between px-4 py-2 border rounded-lg cursor-pointer hover:border-primary bg-gray-50/50 transition-all duration-200"
                    :class="
                      dates.length > 0 ? 'border-primary ring-2 ring-blue-50' : 'border-gray-200'
                    "
                  >
                    <div class="flex items-center overflow-hidden">
                      <v-icon
                        left
                        x-small
                        :color="dates.length > 0 ? 'primary' : 'gray-400'"
                        class="mr-2"
                        >mdi-calendar</v-icon
                      >
                      <span
                        class="text-sm truncate"
                        :class="dates.length > 0 ? 'text-gray-800 font-bold' : 'text-gray-400'"
                      >
                        {{ dateRangeText || 'Select Date Range' }}
                      </span>
                    </div>
                    <v-icon x-small color="gray-300">mdi-chevron-down</v-icon>
                  </div>
                </template>
                <v-date-picker
                  v-model="dates"
                  range
                  no-title
                  color="primary"
                  @input="onDateInput"
                ></v-date-picker>
              </v-menu>
            </v-col>

            <!-- BS Format -->
            <v-col
              cols="12"
              md="4"
              v-if="
                selectedReport.title === 'Balance Sheet Report' ||
                selectedReport.title === 'Vertical Balance Sheet Report'
              "
            >
              <v-select
                v-model="balanceSheetFormat"
                :items="[
                  { text: 'Horizontal (Standard)', value: 'horizontal' },
                  { text: 'Vertical (Tree)', value: 'vertical' }
                ]"
                label="Layout Format"
                dense
                outlined
                hide-details
                color="primary"
                class="rounded-lg bg-gray-50/50"
                @change="refreshPreview"
              ></v-select>
            </v-col>

            <!-- Assignee -->
            <v-col cols="12" md="4" v-if="showLoanFilters">
              <v-autocomplete
                v-model="collectionFilter.assignedTo"
                :items="allUsers"
                item-text="fullName"
                item-value="_id"
                label="Select Assignee"
                placeholder="All Assignees"
                outlined
                dense
                hide-details
                clearable
                class="bg-gray-50/50 rounded-lg"
                @change="refreshPreview"
              >
                <template v-slot:prepend-inner>
                  <v-icon small class="mr-1">mdi-account-search</v-icon>
                </template>
              </v-autocomplete>
            </v-col>

            <!-- SubCase -->
            <v-col cols="12" md="4" v-if="selectedReport.type === 'collection'">
              <v-select
                v-model="collectionFilter.subCase"
                :items="filteredSubCases"
                item-text="name"
                item-value="_id"
                label="Select Sub Case"
                placeholder="All Sub Cases"
                outlined
                dense
                hide-details
                clearable
                class="bg-gray-50/50 rounded-lg"
                @change="refreshPreview"
              >
                <template v-slot:prepend-inner>
                  <v-icon x-small class="mr-1">mdi-filter-variant</v-icon>
                </template>
              </v-select>
            </v-col>

            <!-- Branch -->
            <v-col cols="12" md="4" v-if="selectedReport.type">
              <v-autocomplete
                v-model="collectionFilter.branch"
                :items="branches"
                item-text="branchName"
                item-value="_id"
                label="Select Branch"
                placeholder="All Branches"
                outlined
                dense
                hide-details
                clearable
                class="bg-gray-50/50 rounded-lg"
                @change="refreshPreview"
              >
                <template v-slot:prepend-inner>
                  <v-icon small class="mr-1 mt-1">mdi-map-marker</v-icon>
                </template>
              </v-autocomplete>
            </v-col>

            <!-- Loan Search -->
            <v-col cols="12" md="4" v-if="showLoanFilters">
              <v-text-field
                v-model="collectionFilter.loanSearch"
                label="Search Loan/App No"
                placeholder="Loan/App No"
                outlined
                dense
                hide-details
                clearable
                class="bg-gray-50/50 rounded-lg"
                @keyup.enter="refreshPreview"
                @input="onSearchInput"
              >
                <template v-slot:prepend-inner>
                  <v-icon small class="mr-1 mt-1">mdi-magnify</v-icon>
                </template>
              </v-text-field>
            </v-col>

            <!-- Visit Status -->
            <v-col cols="12" md="4" v-if="selectedReport.title === 'Visit Report'">
              <v-select
                v-model="collectionFilter.status"
                :items="['PENDING', 'IN_PROGRESS', 'COMPLETED']"
                label="Visit Status"
                placeholder="All Statuses"
                outlined
                dense
                hide-details
                clearable
                class="bg-gray-50/50 rounded-lg"
                @change="refreshPreview"
              >
                <template v-slot:prepend-inner>
                  <v-icon x-small class="mr-1">mdi-check-circle-outline</v-icon>
                </template>
              </v-select>
            </v-col>
          </v-row>
        </div>
      </template>

      <!-- Integrated Preview Slot -->
      <template #preview-content="{ rows, headers, html }">
        <div class="report-preview-container bg-white rounded-lg border border-gray-100 p-2 overflow-auto" style="min-height: 500px">
          <template v-if="isBalanceSheetPreview">
            <BalanceSheetHorizontalTemplate
              v-if="selectedReport.title === 'Balance Sheet Report'"
              :data="rows"
              @account-click="handleAccountLink"
            />
            <BalanceSheetVerticalTemplate
              v-else
              :data="rows"
              @account-click="handleAccountLink"
            />
          </template>
          <div v-else-if="html" v-html="html" @click="handlePreviewAccountClick"></div>
          <v-data-table
            v-else-if="rows && rows.length"
            :headers="headers"
            :items="rows"
            flat
            dense
            fixed-header
            height="500px"
            class="elevation-0"
            hide-default-footer
            disable-pagination
          >
            <template v-slot:[`item.${loanNoPreviewKey}`]="{ item }">
              <a @click="openLoanDetail(item)" class="blue--text cursor-pointer font-weight-bold">
                {{ item[loanNoPreviewKey] }}
              </a>
            </template>
          </v-data-table>
          <div v-else class="flex flex-col items-center justify-center py-20 text-gray-400">
            <template v-if="hasFetchedPreview">
              <v-icon size="64" color="gray lighten-3">mdi-database-off-outline</v-icon>
              <p class="mt-4 text-lg">No data available for the selected filters</p>
            </template>
            <template v-else>
              <v-icon size="64" color="gray lighten-3">mdi-file-eye-outline</v-icon>
              <p class="mt-4 text-lg">Report preview will appear here</p>
            </template>
          </div>
        </div>
      </template>
    </GenericExportColumns>

    <!-- (Preview Dialog Removed - Integrated into GenericExportColumns) -->

    <!-- Hidden Container for Balance Sheet PDF Export -->
    <div v-if="isExportingPdf" style="position: absolute; left: -9999px; top: -9999px;">
      <div ref="bsPdfExportContainer">
        <BalanceSheetHorizontalTemplate
          v-if="selectedReport.title === 'Balance Sheet Report'"
          :data="previewRows"
        />
        <BalanceSheetVerticalTemplate
          v-else-if="selectedReport.title === 'Vertical Balance Sheet Report'"
          :data="previewRows"
        />
      </div>
    </div>
  </div>
</template>

<script>
import EmployeeReportdialog from '@/components/EmployeeReportdialog.vue'
import ActitiveTabcomponent from '@/components/ActitiveTabcomponent.vue'
import GenericExportColumns from '@/components/common/GenericExportColumns.vue'
import dayjs from 'dayjs'
import { collectionService } from '@/services/collection'
import { axiosUrl } from '@/utils/workforceApi'
import { downloadExcelJs, isModuleVisible, downloadPdf } from '@/utils/common'
import { COLUMN_MAPPERS, REPORT_COLUMNS, getAssetInfo } from '@/utils/reportConfig'
import { generateCibilTxt, generateCibilUcrfTxt, downloadCibilTxt } from '@/utils/cibilGenerator'
import { isModuleFeatureAllowed, sortDropdownOptions } from '@/utils/common'
import ExcelJS from 'exceljs'
import {
  ATTENDANCE_COLUMN_MAPPERS,
  processAttendanceItems,
  ATTENDANCE_VALID_COLS
} from '@/utils/workforce/attendanceReportUtils'
import html2pdf from 'html2pdf.js'

export default {
  name: 'ReportsDashboard',
  data() {
    return {
      showExportDialog: false,
      showPreviewDialog: false,
      previewRows: [],
      previewHeaders: [],
      previewHtml: '',
      addDialog: false,
      isEmployeeReportDialogOpen: false,
      isExportingPdf: false,
      hasFetchedPreview: false,
      balanceSheetFormat: 'horizontal',
      form: {
        reportName: '',
        selectedReport: '',
        scheduleTime: '',
        dataOwner: ''
      },
      selectedReport: {
        title: '',
        type: ''
      },
      currentExportKeys: [],
      currentExportCategories: [],
      currentSelectedColumns: [], // Added to track columns during preview
      processingReport: null, // Added processingReport
      reportTabs: [
        { key: 'download', label: 'Download Reports', icon: 'mdi-download', component: null },
        { key: 'schedule', label: 'Schedule Reports', icon: 'mdi-calendar-clock', component: null }
      ],
      reportHeaders: [
        { text: 'Report Name', value: 'name' },
        { text: 'Type', value: 'type' },
        { text: 'Time', value: 'time' },
        { text: 'Status', value: 'status' },
        { text: 'Actions', value: 'actions', sortable: false }
      ],
      reportItems: [
        {
          name: 'Collection Performance',
          type: 'Loan Collection',
          time: 'Weekly',
          status: 'Active'
        },
        {
          name: 'EMI Status Check',
          type: 'EMI DUE',
          time: 'Daily',
          status: 'Active'
        }
      ],
      reportOptions: [
        'Loan Collection Report',
        'Daily Collection Status Report',
        'EMI DUE Report',
        'Detailed Due Report',
        'Non Starter Report',
        'Loan Expiry Report',
        'VAS Report',
        'Future payment report',
        'Loan PAR Report',
        'EMI Tracker Report',
        'Cash Inflow Report',
        'Cash Outflow Report',
        'Business Done Report',
        'Loan Closure Report',
        'CIBIL TUDF Format',
        'CIBIL UCRF Format',
        'Attendance Report',
        'Muster Report-I',
        'Muster Report-II'
      ],
      scheduleOptions: ['Daily', 'Weekly', 'Monthly'],
      showFilterDialog: false,
      dates: [],
      dateMenu: false,
      attendanceSettings: null,
      allUsers: [],
      allSubCases: [],
      collectionFilter: {
        assignedTo: null,
        subCase: null,
        status: null,
        branch: null,
        loanSearch: null
      },
      branches: []
    }
  },
  watch: {
    balanceSheetFormat(newVal) {
      if (
        this.selectedReport.title === 'Balance Sheet Report' ||
        this.selectedReport.title === 'Vertical Balance Sheet Report'
      ) {
        const actualTitle =
          newVal === 'vertical' ? 'Vertical Balance Sheet Report' : 'Balance Sheet Report'
        this.selectedReport.title = actualTitle
        this.currentExportKeys = REPORT_COLUMNS[actualTitle]?.columns || []
      }
    },
    'collectionFilter.subCase': {
      handler() {
        this.updateReportHeaders()
      }
    },
    'form.dataOwner': {
      handler(newVal) {
        if (newVal !== 'specific Users') {
          this.isEmployeeReportDialogOpen = false
        }
      },
      deep: true
    }
  },
  computed: {
    isBalanceSheetPreview() {
      return (
        ['Balance Sheet Report', 'Vertical Balance Sheet Report'].includes(
          this.selectedReport.title
        ) && (this.previewRows && this.previewRows.length > 0)
      )
    },
    showLoanFilters() {
      const loanTypes = ['collection', 'loan_management']
      const excludeTitles = [
        'Profit and Loss Report',
        'Trial Balance Report',
        'Balance Sheet Report',
        'Vertical Balance Sheet Report',
        'CIBIL TUDF Format',
        'CIBIL UCRF Format'
      ]

      if (loanTypes.includes(this.selectedReport.type)) return true
      if (
        this.selectedReport.type === 'financial' &&
        !excludeTitles.includes(this.selectedReport.title)
      ) {
        return true
      }
      return false
    },
    loanNoPreviewKey() {
      if (!this.previewHeaders || !this.previewHeaders.length) return null
      const h = this.previewHeaders.find(
        (h) =>
          h.value &&
          (h.value.startsWith('loan_account_no') ||
            h.value.startsWith('case_no') ||
            h.value.startsWith('loan_no') ||
            h.value.startsWith('loan_account_number') ||
            h.value.startsWith('applicationNo'))
      )
      return h ? h.value : null
    },
    filteredSubCases() {
      return this.allSubCases.filter((s) => s.subCaseStatus === 'ACTIVE')
    },
    dateRangeText() {
      if (!this.dates || !this.dates.length) return ''
      if (this.dates.length === 1) return this.dates[0]
      return `${this.dates[0]} to ${this.dates[1]}`
    }
  },

  components: {
    EmployeeReportdialog,
    ActitiveTabcomponent,
    GenericExportColumns,
    BalanceSheetHorizontalTemplate: () => import('./Accounting/BalanceSheetHorizontalTemplate.vue'),
    BalanceSheetVerticalTemplate: () => import('./Accounting/BalanceSheetVerticalTemplate.vue')
  },

  methods: {
    sectionVisible(section) {
      return isModuleVisible(section)
    },
    handleTabChange(t) {
      console.log(t)
    },
    openReportDialog(title, type) {
      this.selectedReport = { title, type }
      if (title === 'Balance Sheet Report') {
        this.balanceSheetFormat = 'horizontal'
      }
      this.currentExportKeys = REPORT_COLUMNS[title]?.columns || []
      this.currentExportCategories = REPORT_COLUMNS[title]?.categories || []
      this.showExportDialog = true
      this.hasFetchedPreview = false
      this.dates = [] // Reset dates
      this.collectionFilter = { assignedTo: null, subCase: null, status: null } // Reset filters

      if (['collection', 'loan_management', 'financial'].includes(type)) {
        this.fetchFilterData()
      }
    },
    async fetchFilterData() {
      try {
        if (!this.allUsers.length) {
          const usersResp = await this.$axios.get('/v3/users', { params: { limit: 1000 } })
          this.allUsers = usersResp.data?.users || usersResp.data?.data || []
        }
        if (!this.allSubCases.length) {
          const subcaseResp = await collectionService.getExtendedUsecases()
          this.allSubCases = subcaseResp.data?.data || []
        }
        if (!this.branches.length) {
          const branchResp = await this.$axios.get(axiosUrl.branches.getBranches)
          this.branches = branchResp.data?.branches || []
        }

        if (this.selectedReport.type === 'collection' && !this.collectionFilter.subCase) {
          if (this.filteredSubCases.length > 0) {
            this.collectionFilter.subCase = this.filteredSubCases[0]._id
          }
        }
        this.updateReportHeaders()
      } catch (err) {
        console.error('Failed to fetch filter data', err)
      }
    },
    updateReportHeaders() {
      if (!this.collectionFilter.subCase || !this.allSubCases.length) return

      const subCase = this.allSubCases.find((s) => s._id === this.collectionFilter.subCase)
      if (!subCase) return

      const fm = {
        ...subCase.baseFieldMappings,
        ...subCase.collectionManagementFieldMapping,
        ...subCase.loanManagementFieldMapping,
        ...subCase.customerFieldMappings
      }

      const getText = (field, defaultText) => field?.displayName || defaultText

      const caseTitle = subCase.tableTabMapping?.caseTable?.displayName || 'Case'

      if (this.selectedReport.title === 'Collection Report') {
        this.currentExportKeys = [
          { text: getText(fm.caseNo, `${caseTitle} No`), value: 'case_no' },
          { text: getText(fm.crn, 'CRN'), value: 'crn' },
          { text: getText(fm.customerFullName, 'Customer Name'), value: 'customer_name' },
          { text: getText(fm.customerMobile, 'Customer Mobile'), value: 'customer_mobile' },
          { text: getText(fm.typeOfLoan, `${caseTitle} Type`), value: 'loan_type' },
          { text: getText(fm.principalOutstanding, 'POS'), value: 'outstanding_principal' },
          { text: getText(fm.emiAmount, 'EMI Amount'), value: 'emi_amount_due' },
          { text: getText(fm.bucket, 'Bucket'), value: 'bucket' },
          { text: getText(fm.tenure, 'Tenure'), value: 'tenure' },
          { text: getText(fm.productInfo, 'Product Info'), value: 'product_info' },
          { text: getText(fm.assignedTo, 'Assigned To'), value: 'agent_name' },
          { text: 'Agent ID', value: 'agent_id' },
          { text: getText(fm.currentStatus, 'Status'), value: 'emi_status' },
          { text: getText(fm.caseOutcomeStatus, 'Outcome'), value: 'outcome' },
          { text: 'Start Date', value: 'case_start_date' },
          { text: 'Closed Date', value: 'case_end_date' },
          { text: 'TAT', value: 'tat' }
        ]

        if (subCase.additionalColumns) {
          Object.values(subCase.additionalColumns).forEach((col) => {
            this.currentExportKeys.push({
              text: col.displayName || col.key,
              value: `additional_${col.key}`
            })
          })
        }
      } else if (this.selectedReport.title === 'Visit Report') {
        const visitTitle = subCase.tableTabMapping?.visitTable?.displayName || 'Visit'
        this.currentExportKeys = [
          { text: `${visitTitle} Title`, value: 'visit_title' },
          { text: getText(fm.caseNo, 'Case No'), value: 'case_no' },
          { text: getText(fm.customerFullName, 'Customer Name'), value: 'customer_name' },
          { text: `${visitTitle} Status`, value: 'visit_status' },
          { text: 'Started At', value: 'visit_started' },
          { text: 'Completed At', value: 'visit_completed' },
          { text: getText(fm.assignedTo, 'Assigned To'), value: 'visit_assigned_to' },
          { text: 'Meeting Outcome', value: 'meeting_outcome' }
        ]
      }
    },
    async handleExportData(selectedColumns) {
      await this.handleReportAction(selectedColumns, 'export')
    },
    async handlePreviewData(selectedColumns) {
      this.currentSelectedColumns = selectedColumns
      if (this.$refs.exportDialog) {
        this.$refs.exportDialog.viewMode = 'preview'
      }
      await this.handleReportAction(selectedColumns, 'preview')
    },
    onDateInput() {
      if (this.dates.length === 2) {
        this.dateMenu = false
        this.refreshPreview()
      }
    },
    onSearchInput() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.refreshPreview()
      }, 600)
    },
    refreshPreview() {
      // Use nextTick to ensure v-model values (like cleared inputs) are updated before fetching
      this.$nextTick(() => {
        this.handleReportAction(this.currentSelectedColumns, 'preview')
      })
    },
    openLoanDetail(item) {
      if (!item) return
      const loanId = item._id || item.loanId || item.loan?._id || item.loan?.id || item.loan_id
      const subCaseId =
        this.collectionFilter.subCase ||
        item.subCaseId ||
        item.subCase ||
        item.loan?.subCaseId ||
        (this.filteredSubCases.length ? this.filteredSubCases[0]._id : null)
      const loanNoKey = this.loanNoPreviewKey
      const loanNo = loanNoKey ? item[loanNoKey] : null

      if (!subCaseId) {
        this.$toast?.error('Sub Case not identified for navigation')
        return
      }

      const routeData = this.$router.resolve({
        name: 'DynamicUseCase',
        params: {
          useCase: 'collection',
          subCase: subCaseId
        },
        query: {
          loanSearch: loanNo,
          activeTab: 'Cases',
          id: loanId
        }
      })
      window.open(routeData.href, '_blank')
    },
    async handleReportAction(selectedColumns, actionType = 'export') {
      if (actionType !== 'preview') {
        this.showExportDialog = false
      }
      const reportTitle = this.selectedReport.title
      this.processingReport = reportTitle // Set processingReport
      const reportConfig = REPORT_COLUMNS[reportTitle]

      if (!reportConfig) {
        console.error('No configuration found for report:', reportTitle)
        this.processingReport = null
        return
      }

      try {
        this.hasFetchedPreview = true
        if (reportConfig.apiType === 'attendance') {
          await this.handleAttendanceExport(selectedColumns, actionType)
          return
        }

        const filters = {}
        if (this.dates && this.dates.length === 2) {
          filters.start = this.dates.sort()[0]
          filters.end = this.dates.sort()[1]
        }
        if (this.collectionFilter.branch) {
          filters.branch = this.collectionFilter.branch
        }
        if (this.collectionFilter.loanSearch && String(this.collectionFilter.loanSearch).trim()) {
          filters.search = String(this.collectionFilter.loanSearch).trim()
        }

        // Fetch Asset Classification via API
        let assetClassification = []
        try {
          const assetResp = await collectionService.getAssetClassificationRules()
          assetClassification = assetResp.data?.rules || assetResp.data?.data || []
        } catch (err) {
          console.error('Failed to fetch asset classification', err)
        }

        let response
        let rawData = []

        if (reportConfig.apiType === 'emi-details') {
          response = await collectionService.getEmiDetails(
            1,
            5000, // Fetch more records for specialized reports
            filters
          )
          rawData = response.data?.emiDetails || []
        } else if (reportConfig.apiType === 'collection-data') {
          response = await collectionService.getCollectionData(1, 5000, {
            ...filters,
            subCase: this.collectionFilter.subCase,
            assignedTo: this.collectionFilter.assignedTo
          })
          rawData = response.data?.collections || []
        } else if (reportConfig.apiType === 'collection-tasks') {
          response = await collectionService.getCaseTasks(1, 5000, {
            ...filters,
            subCase: this.collectionFilter.subCase,
            assignedTo: this.collectionFilter.assignedTo,
            taskStatus: this.collectionFilter.status
          })
          rawData = response.data?.tasks || []
        } else if (reportConfig.apiType === 'trial-balance') {
          const [groupsResp, summaryResp] = await Promise.all([
            this.$axios.get(axiosUrl.ledgerGroups.getLedgerGroups),
            this.$axios.get('/orgSetup/accounts-management/summary')
          ])
          rawData = {
            groups: groupsResp.data?.data || groupsResp.data?.ledgerGroups || [],
            summary: summaryResp.data?.summary || { ledgerWise: [] },
            assetClassification // Add to rawData
          }
        } else if (reportConfig.apiType === 'accounts-management') {
          response = await this.$axios.get('/orgSetup/accounts-management')
          rawData = response.data?.accountsManagements || []
        } else {
          const subCaseId =
            this.collectionFilter.subCase ||
            (this.filteredSubCases.length > 0
              ? this.filteredSubCases[0]._id
              : '68724b5d8e15f2046ee23837')
          response = await collectionService.getLoanCollection(subCaseId, 1, 5000, filters)
          rawData = response.data?.loans || []
        }

        if (!rawData || (Array.isArray(rawData) && rawData.length === 0)) {
          this.$toast?.warning('No data found for the selected range')
          if (actionType === 'preview') {
            this.previewRows = []
            this.previewHeaders = []
            this.previewHtml = ''
          }
          return
        }

        // Augment with Asset Classification info for all reports
        const augmentLoan = (l) => {
          if (l) l.assetInfo = getAssetInfo(l, assetClassification)
        }

        if (Array.isArray(rawData)) {
          rawData.forEach((item) => {
            if (item.loan) augmentLoan(item.loan)
            else augmentLoan(item)
          })
        } else if (rawData && typeof rawData === 'object') {
          if (rawData.loans && Array.isArray(rawData.loans)) {
            rawData.loans.forEach(augmentLoan)
          }
          rawData.assetClassification = assetClassification
        }

        const dataUnits = reportConfig.getRows(rawData)
        const finalColumns = selectedColumns.map((colValue, idx) => {
          const colDef = this.currentExportKeys.find((c) => c.value === colValue)
          return {
            header: colDef ? colDef.text : colValue,
            key: `${colValue}_${idx}`
          }
        })

        const rows = dataUnits.map((unit) => {
          const row = { ...unit }
          selectedColumns.forEach((colValue, idx) => {
            const key = `${colValue}_${idx}`
            const mapper = COLUMN_MAPPERS[colValue]

            if (mapper) {
              row[key] = mapper(unit)
            } else if (typeof colValue === 'string' && colValue.startsWith('additional_')) {
              const fieldKey = colValue.replace('additional_', '')
              row[key] = unit.loan?.additionalColumns?.[fieldKey] || '-'
            } else if (unit[colValue] !== undefined && unit[colValue] !== null) {
              // Direct field read — used for balance sheet reports (vbs_account, vbs_total etc)
              row[key] = unit[colValue]
            } else {
              row[key] = '-'
            }
          })
          if (unit.isSummary || unit._isSummary) row._isSummary = true
          return row
        })

        if (rows.length === 0) {
          this.$toast?.warning('No matching records found for this report criteria')
          if (actionType === 'preview') {
            this.previewRows = []
            this.previewHeaders = []
            this.previewHtml = ''
          }
          return
        }

        if (actionType === 'preview') {
          const bsReports = ['Balance Sheet Report', 'Vertical Balance Sheet Report']
          if (bsReports.includes(reportTitle)) {
            this.previewHtml = ''
            this.previewRows = rows
          } else {
            this.previewHtml = ''
            this.previewHeaders = finalColumns.map((c) => ({
              text: c.header,
              value: c.key,
              width: '200px'
            }))
            this.previewRows = rows
          }
          this.processingReport = null
          return
        }

        // Special handling for CIBIL Text Files
        if (reportTitle.includes('CIBIL')) {
          if (reportTitle === 'CIBIL TUDF Format') {
            const cibilContent = generateCibilTxt(rawData)
            downloadCibilTxt(cibilContent, `CIBIL_TUDF_${dayjs().format('MMM_YYYY')}.txt`)
            this.$toast?.success('Bureau TUDF file generated successfully')
          } else if (reportTitle === 'CIBIL UCRF Format') {
            const cibilContent = generateCibilUcrfTxt(rawData)
            downloadCibilTxt(cibilContent, `CIBIL_UCRF_${dayjs().format('MMM_YYYY')}.txt`)
            this.$toast?.success('Bureau UCRF file generated successfully')
          }
        }

        if (!reportTitle.includes('CIBIL')) {
          const pdfReports = [
            'Trial Balance Report',
            'Profit and Loss Report',
            'Balance Sheet Report',
            'Vertical Balance Sheet Report'
          ]
          if (pdfReports.includes(reportTitle)) {
            const bsReports = ['Balance Sheet Report', 'Vertical Balance Sheet Report']
            if (bsReports.includes(reportTitle)) {
              this.isExportingPdf = true
              this.previewRows = rows
              this.$nextTick(async () => {
                try {
                  const element = this.$refs.bsPdfExportContainer
                  const opt = {
                    margin: [10, 10, 10, 10],
                    filename: `${reportTitle.replace(/\s+/g, '_')}.pdf`,
                    image: { type: 'jpeg', quality: 0.98 },
                    html2canvas: { scale: 2, useCORS: true },
                    jsPDF: {
                      unit: 'mm',
                      format: 'a4',
                      orientation: reportTitle.includes('Vertical') ? 'portrait' : 'landscape'
                    }
                  }
                  await html2pdf().set(opt).from(element).save()
                  this.$toast?.success('PDF generated successfully')
                } catch (err) {
                  console.error('PDF Export failed:', err)
                  this.$toast?.error('Failed to generate PDF')
                } finally {
                  this.isExportingPdf = false
                }
              })
            } else {
              await downloadPdf(
                rows,
                reportTitle,
                `${reportTitle.replace(/\s+/g, '_')}.pdf`,
                finalColumns
              )
            }
          } else {
            await downloadExcelJs(
              rows,
              reportTitle,
              `${reportTitle.replace(/\s+/g, '_')}.xlsx`,
              finalColumns
            )
          }
        }
      } catch (error) {
        console.error(`${actionType === 'preview' ? 'Preview' : 'Export'} failed:`, error)
        this.$toast?.error(
          `Failed to ${actionType === 'preview' ? 'prepare preview' : 'generate report'}`
        )
      } finally {
        this.processingReport = null // Reset processingReport
      }
    },
    saveChanges() {
      // Mock save logic
      console.log('Saving changes:', this.form)
      this.addDialog = false
    },
    deleteReport(item) {
      this.reportItems = this.reportItems.filter((i) => i !== item)
    },
    getReportIcon(title) {
      if (title.includes('Collection')) return 'mdi-cash-register'
      if (title.includes('EMI')) return 'mdi-clock-alert'
      if (title.includes('Detailed')) return 'mdi-file-find'
      if (title.includes('Attendance')) return 'mdi-calendar-check'
      if (title.includes('Muster')) return 'mdi-file-table-outline'
      return 'mdi-file-chart'
    },

    handleAccountLink(accountName) {
      const url = `/workforce/accounts?search=${encodeURIComponent(accountName)}`
      window.open(url, '_blank')
    },

    handlePreviewAccountClick(event) {
      const target = event.target.closest('[data-account-link]')
      if (!target) return
      const accountName = target.getAttribute('data-account-link')
      if (!accountName) return
      const url = `/workforce/accounts?search=${encodeURIComponent(accountName)}`
      window.open(url, '_blank')
    },

    openLoanFromPreview(loanNo) {
      if (!loanNo || loanNo === '-') return

      // Get the subCase used when generating this report
      const subCaseId =
        this.collectionFilter.subCase ||
        (this.filteredSubCases.length > 0 ? this.filteredSubCases[0]._id : null)

      const subCase = subCaseId
        ? this.allSubCases.find((s) => s._id === subCaseId)
        : null

      if (subCase) {
        const useCase = subCase.useCase || 'collection'
        const url = `/workforce/${useCase}/${subCase._id}?loanSearch=${encodeURIComponent(loanNo)}`
        window.open(url, '_blank')
      } else {
        // Fallback: open reports with the loan no pre-filled in search filter
        window.open(`/workforce/reports?loanSearch=${encodeURIComponent(loanNo)}`, '_blank')
      }
    },

    async handleAttendanceExport(selectedColumns, actionType = 'export') {
      const reportTitle = this.selectedReport.title
      const start = this.dates.sort()[0]
      const end = this.dates.sort()[1]

      try {
        // 1. Fetch Users efficiently
        this.$toast?.info('Fetching users list...')
        let allUsers = []
        let page = 1
        let hasMore = true
        while (hasMore) {
          const usersResp = await this.$axios.get('/v3/users', {
            params: { limit: 1000, page }
          })
          const fetchedData = usersResp.data?.users || usersResp.data?.data || []
          if (fetchedData.length > 0) {
            allUsers = [...allUsers, ...fetchedData]
            page++
            if (fetchedData.length < 1000) hasMore = false
          } else {
            hasMore = false
          }
        }

        if (allUsers.length === 0) {
          this.$toast?.warning('No users found.')
          return
        }

        if (reportTitle === 'Attendance Report' || reportTitle === 'Muster Report-I') {
          this.$toast?.info('Fetching attendance records. This may take a moment...')

          if (!this.attendanceSettings) {
            try {
              const { data } = await this.$axios.get('/orgSetup/attendance-settings')
              this.attendanceSettings = data.attendanceSettings || {}
            } catch (err) {
              console.error('Failed to fetch attendance settings', err)
            }
          }

          // Fetch Attendance Summary using efficient chunking
          const attendanceData = await this.fetchAttendanceInChunks(allUsers, start, end)

          // Combine Data using Generic Utility
          const items = processAttendanceItems(
            allUsers,
            attendanceData,
            this.attendanceSettings,
            process.env.VUE_APP_S3_BASE_URL
          )

          const currentMonthDates = []
          let curr = dayjs(start)
          const endDay = dayjs(end)
          while (curr.isBefore(endDay) || curr.isSame(endDay, 'day')) {
            currentMonthDates.push(curr.format('YYYY-MM-DD'))
            curr = curr.add(1, 'day')
          }

          if (actionType === 'preview') {
            this.$toast?.info(
              'Preview is currently available for Loan/Financial reports only. Downloading full report instead.'
            )
          }

          if (reportTitle === 'Attendance Report') {
            await this.generateAttendanceMatrix(items, currentMonthDates, selectedColumns)
          } else {
            await this.generateMusterReportI(items, currentMonthDates, selectedColumns)
          }
        } else if (reportTitle === 'Muster Report-II') {
          this.$toast?.info(
            'Fetching granular activity data for all users. This may take a few minutes...'
          )

          // 1. Calculate Max Managers
          const reporteeChain = this.$storage?.getUniversal('userReporteeChain') || {}
          let maxManagers = 0
          allUsers.forEach((u) => {
            const chain = reporteeChain[u.orgEmployeeId || u.employeeId]?.managerChain || ''
            const list = chain ? chain.split(' -> ') : []
            maxManagers = Math.max(maxManagers, list.length)
          })

          // 2. Generate Export Structure (Categorization)
          const structure = { mainHeaders: [], subHeaders: [], keys: [] }
          const categories = [
            {
              title: 'Employee Details',
              columns: ['Employee Name', 'Emp Id', 'Department', 'Designation', 'City']
            },
            { title: 'Manager Details', columns: ['Direct Manager', 'Manager Hierarchy'] },
            {
              title: 'Daily Attendance Reports',
              columns: [
                'Date',
                'Attendance',
                'First Clock-In',
                'Last Clock-Out',
                'Shift Start Time',
                'Planned Working Hour',
                'Completed Working Hour',
                'Breaks Count',
                'Overtime',
                'Delay In Shift',
                'Clock-In Image',
                'Clock-Out Image'
              ]
            },
            {
              title: 'Activity Details',
              columns: [
                'Activity Date',
                'Activity',
                'Activity Start Time',
                'Activity End Time',
                'Duration of Activity',
                'Activity Start Location',
                'Activity End Location'
              ]
            },
            {
              title: 'Visit Details',
              columns: [
                'Visit Date',
                'Customer Name',
                'Arrival Time',
                'Departure Time',
                'Duration of Visit',
                'Visit Location',
                'Daily Total Visits',
                'Distance Breakup Per Day',
                'Total Distance Per Day',
                'Mode of Transportation',
                'Notes/Comments'
              ]
            },
            {
              title: 'Monthly Travel Summary',
              columns: ['Month', 'Total Distance Traveled', 'Total Visits Done']
            }
          ]

          categories.forEach((cat, idx) => {
            const matched = []
            if (cat.title === 'Manager Details') {
              if (selectedColumns.includes('Direct Manager')) matched.push('Direct Manager')
              if (selectedColumns.includes('Manager Hierarchy')) {
                for (let i = 1; i <= maxManagers; i++) matched.push(`Manager Level ${i}`)
                matched.push('Manager Hierarchy')
              }
            } else {
              cat.columns.forEach((col) => {
                if (selectedColumns.includes(col)) matched.push(col)
              })
            }

            if (matched.length > 0) {
              const start = structure.subHeaders.length + 1
              structure.subHeaders.push(...matched)
              structure.keys.push(...matched)
              structure.mainHeaders.push({
                title: cat.title,
                start,
                end: start + matched.length - 1,
                color: idx % 2 === 0 ? 'FFCFE2F3' : 'FFD9EAD3'
              })
            }
          })

          const workbook = new ExcelJS.Workbook()
          const worksheet = workbook.addWorksheet('Muster Report II')

          // Header Row 1: Main Categories
          const mainHeaderRow = worksheet.getRow(1)
          structure.mainHeaders.forEach((mh) => {
            worksheet.mergeCells(1, mh.start, 1, mh.end)
            const cell = mainHeaderRow.getCell(mh.start)
            cell.value = mh.title
            cell.font = { size: 14, bold: true }
            cell.alignment = { horizontal: 'center', vertical: 'middle' }
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: mh.color } }
          })
          mainHeaderRow.height = 30

          // Header Row 2: Sub Headers
          const subHeaderRow = worksheet.getRow(2)
          subHeaderRow.values = structure.subHeaders
          subHeaderRow.font = { bold: true, size: 12 }
          subHeaderRow.alignment = { horizontal: 'center', vertical: 'middle' }
          subHeaderRow.height = 25

          // Fetch data concurrently
          const [userTimeSeriesData, attendanceRecords] = await Promise.all([
            this.fetchTimeSeriesConcurrently(allUsers, start, end),
            this.fetchAttendanceInChunks(allUsers, start, end)
          ])

          // Map attendance records: userId -> Date -> status
          const attendanceMap = {}
          attendanceRecords.forEach((att) => {
            const uId = att.userId?._id || att.userId
            const d = dayjs(att.attendanceDate || att.attendanceDateUTC).format('YYYY-MM-DD')
            if (!attendanceMap[uId]) attendanceMap[uId] = {}
            attendanceMap[uId][d] = att.attendanceStatus
          })

          let srNo = 1
          const dates = []
          let curr = dayjs(start)
          while (curr.isBefore(dayjs(end)) || curr.isSame(dayjs(end), 'day')) {
            dates.push(curr.format('YYYY-MM-DD'))
            curr = curr.add(1, 'day')
          }

          for (const user of allUsers) {
            const userPoints = userTimeSeriesData[user._id] || []
            const reporteeChain = this.$storage?.getUniversal('userReporteeChain') || {}
            const chainObj =
              reporteeChain[user.orgEmployeeId] ||
              reporteeChain[user.employeeId] ||
              reporteeChain[user._id] ||
              {}
            const managerChain = chainObj.managerChain || ''
            const managerList = managerChain ? managerChain.split(' -> ') : []

            const employeeData = {} // Date-wise buckets
            userPoints.forEach((p) => {
              const d = dayjs(p.timestamp || p.createdAt).format('YYYY-MM-DD')
              if (!employeeData[d]) employeeData[d] = []
              employeeData[d].push(p)
            })

            // Calculate monthly totals for summary
            let totalDistance = 0
            let totalVisits = 0
            dates.forEach((d) => {
              const pts = employeeData[d] || []
              totalDistance += this.calculateTotalDistance(pts)
              const visits = new Set()
              pts.forEach((p) => {
                if (p.activity?.caseTaskDetails)
                  visits.add(p.activity.caseTaskDetails._id || p.activity.caseTaskDetails)
              })
              totalVisits += visits.size
            })

            dates.forEach((date, dateIdx) => {
              const pts = employeeData[date] || []
              const statusFromMap = attendanceMap[user._id]?.[date] || null
              const dayData = this.processDailyPoints(pts, statusFromMap)

              const constructRow = (pair = null, isFirst = true) => {
                const row = []
                structure.keys.forEach((key) => {
                  if (key.startsWith('Manager Level ')) {
                    const idx = parseInt(key.replace('Manager Level ', '')) - 1
                    row.push(dateIdx === 0 && isFirst ? managerList[idx] || '-' : '')
                    return
                  }

                  switch (key) {
                    case 'Employee Name':
                      row.push(
                        dateIdx === 0 && isFirst
                          ? user.fullName || `${user.firstName} ${user.lastName || ''}`
                          : ''
                      )
                      break
                    case 'Emp Id':
                      row.push(
                        dateIdx === 0 && isFirst ? user.orgEmployeeId || user.employeeId : ''
                      )
                      break
                    case 'Department':
                      row.push(
                        dateIdx === 0 && isFirst ? user.department?.departmentName || '-' : ''
                      )
                      break
                    case 'Designation':
                      row.push(
                        dateIdx === 0 && isFirst ? user.designation?.designationName || '-' : ''
                      )
                      break
                    case 'City':
                      row.push(
                        dateIdx === 0 && isFirst ? user.city || user.address?.city || '-' : ''
                      )
                      break
                    case 'Direct Manager':
                      row.push(dateIdx === 0 && isFirst ? user.reportesTo?.fullName || '-' : '')
                      break
                    case 'Manager Hierarchy':
                      row.push(dateIdx === 0 && isFirst ? managerChain : '')
                      break
                    case 'Date':
                      row.push(isFirst ? dayjs(date).format('DD/MM/YYYY') : '')
                      break
                    case 'Attendance':
                      row.push(isFirst ? dayData.attendanceStatus : '')
                      break
                    case 'First Clock-In':
                      row.push(isFirst ? dayData.firstClockIn : '')
                      break
                    case 'Last Clock-Out':
                      row.push(isFirst ? dayData.lastClockOut : '')
                      break
                    case 'Shift Start Time':
                      row.push(
                        isFirst
                          ? this.attendanceSettings?.shiftStartTime
                            ? dayjs(this.attendanceSettings.shiftStartTime, 'HH:mm').format(
                                'hh:mm A'
                              )
                            : '-'
                          : ''
                      )
                      break
                    case 'Planned Working Hour':
                      row.push(
                        isFirst
                          ? this.attendanceSettings?.workingHours
                            ? `${this.attendanceSettings.workingHours} hrs`
                            : '-'
                          : ''
                      )
                      break
                    case 'Completed Working Hour':
                      row.push(isFirst ? dayData.workingHours : '')
                      break
                    case 'Breaks Count':
                      row.push(isFirst ? dayData.breaksCount : '')
                      break
                    case 'Total Distance Per Day':
                      row.push(isFirst ? dayData.dailyDistance.toFixed(2) : '')
                      break
                    case 'Daily Total Visits':
                      row.push(isFirst ? dayData.dailyVisitCount : '')
                      break
                    case 'Month':
                      row.push(dateIdx === 0 && isFirst ? dayjs(date).format('MMMM YYYY') : '')
                      break
                    case 'Total Distance Traveled':
                      row.push(dateIdx === 0 && isFirst ? totalDistance.toFixed(2) : '')
                      break
                    case 'Total Visits Done':
                      row.push(dateIdx === 0 && isFirst ? totalVisits : '')
                      break

                    // Pair columns
                    case 'Activity Date':
                      row.push(pair ? dayjs(pair.originArrivalTime).format('DD/MM/YYYY') : '-')
                      break
                    case 'Activity':
                      row.push(pair?.originAction || '-')
                      break
                    case 'Activity Start Time':
                    case 'Arrival Time':
                      row.push(
                        pair?.originArrivalTime
                          ? dayjs(pair.originArrivalTime).format('hh:mm A')
                          : '-'
                      )
                      break
                    case 'Activity End Time':
                    case 'Departure Time':
                      row.push(
                        pair?.originDepartureTime
                          ? dayjs(pair.originDepartureTime).format('hh:mm A')
                          : '-'
                      )
                      break
                    case 'Duration of Activity':
                    case 'Duration of Visit':
                      row.push(pair?.originDuration || '-')
                      break
                    case 'Activity Start Location':
                    case 'Visit Location':
                      row.push(pair?.origin || '-')
                      break
                    case 'Activity End Location':
                      row.push(pair?.destination || '-')
                      break
                    case 'Customer Name':
                      row.push(pair?.customerName || '-')
                      break
                    case 'Distance Breakup Per Day':
                      row.push(
                        pair?.segmentDistance !== undefined ? pair.segmentDistance.toFixed(2) : '-'
                      )
                      break
                    default:
                      row.push('-')
                      break
                  }
                })
                return row
              }

              if (dayData.pairs && dayData.pairs.length > 0) {
                dayData.pairs.forEach((p, pIdx) => worksheet.addRow(constructRow(p, pIdx === 0)))
              } else {
                worksheet.addRow(constructRow(null, true))
              }
            })
            srNo++
          }

          worksheet.columns.forEach((col) => {
            col.width = 25
          })
          const buffer = await workbook.xlsx.writeBuffer()
          this.downloadBlob(buffer, `Muster_Report_II_${start}_${end}.xlsx`)
          this.$toast?.success('Report generated successfully!')
        }
      } catch (error) {
        console.error('Attendance export error:', error)
        this.$toast?.error('Failed to generate report. Please try again.')
      }
    },

    async fetchAttendanceInChunks(users, start, end) {
      const results = []
      const batchSize = 5 // max concurrent requests
      const usersPerChunk = 20
      const userIds = users.map((u) => u._id)

      const chunkedUsers = []
      for (let i = 0; i < userIds.length; i += usersPerChunk) {
        chunkedUsers.push(userIds.slice(i, i + usersPerChunk).join(','))
      }

      const paramsArray = []
      chunkedUsers.forEach((uStr) => {
        paramsArray.push({
          start,
          end,
          userIds: uStr,
          limit: 100000
        })
      })

      for (let i = 0; i < paramsArray.length; i += batchSize) {
        const batch = paramsArray.slice(i, i + batchSize)
        const requests = batch.map((p) => this.$axios.get('/user/v2/attendance', { params: p }))
        const responses = await Promise.allSettled(requests)

        responses.forEach((res) => {
          if (res.status === 'fulfilled' && res.value?.data) {
            const dataArr =
              res.value.data.employeeAttendance ||
              res.value.data.data?.data ||
              res.value.data.data ||
              []
            if (Array.isArray(dataArr)) {
              results.push(...dataArr)
            }
          }
        })
      }
      return results
    },

    async fetchTimeSeriesConcurrently(users, start, end) {
      const resultsMap = {}
      const concurrencyLimit = 10 // Max concurrent requests to prevent server overload

      for (let i = 0; i < users.length; i += concurrencyLimit) {
        const batchUsers = users.slice(i, i + concurrencyLimit)

        const requests = batchUsers.map(async (user) => {
          try {
            const tsResp = await this.$axios.get(`/location-time-series/${user._id}`, {
              params: { start, end, limit: 100000 }
            })
            return {
              userId: user._id,
              data: tsResp.data?.data?.data || []
            }
          } catch (err) {
            console.error(`Failed to fetch TS for ${user._id}`, err)
            return { userId: user._id, data: [] }
          }
        })

        const batchResults = await Promise.all(requests)
        batchResults.forEach((res) => {
          resultsMap[res.userId] = res.data
        })
      }
      return resultsMap
    },

    async generateAttendanceMatrix(items, currentMonthDates, selectedColumns) {
      const workbook = new ExcelJS.Workbook()
      const worksheet = workbook.addWorksheet('Attendance')
      const sortedItems = sortDropdownOptions([...items], 'name')
      const start = this.dates.sort()[0]
      const end = this.dates.sort()[1]

      // Fetch manager chain local logic (or from storage)
      const reporteeChain = this.$storage?.getUniversal('userReporteeChain') || {}
      let maxManagers = 0
      sortedItems.forEach((employee) => {
        const chain =
          reporteeChain[employee.orgEmployeeId || employee.employeeId]?.managerChain || ''
        const list = chain ? chain.split(' -> ') : []
        maxManagers = Math.max(maxManagers, list.length)
      })
      const managerHeaders = Array.from({ length: maxManagers }, (_, i) => `Manager Level ${i + 1}`)

      // Header Row 1 (Main)
      const mainHeaderRow = worksheet.getRow(1)
      const subHeaderRow = worksheet.getRow(2)
      let colIndex = 1

      const staticHeaders = [
        'Name',
        'Employee Id',
        'Role',
        'Designation',
        'City',
        ...managerHeaders,
        'Manager Hierarchy'
      ]
      staticHeaders.forEach((header) => {
        const cell = mainHeaderRow.getCell(colIndex)
        cell.value = header
        cell.font = { bold: true }
        worksheet.mergeCells(1, colIndex, 2, colIndex)
        colIndex++
      })

      currentMonthDates.forEach((date) => {
        const startCol = colIndex
        const dateCols = selectedColumns.filter((c) => ATTENDANCE_VALID_COLS.includes(c))

        if (dateCols.length > 0) {
          worksheet.mergeCells(1, startCol, 1, startCol + dateCols.length - 1)
          mainHeaderRow.getCell(startCol).value = date
          mainHeaderRow.getCell(startCol).font = { bold: true }
          mainHeaderRow.getCell(startCol).alignment = { horizontal: 'center' }

          dateCols.forEach((col) => {
            subHeaderRow.getCell(colIndex).value = col
            subHeaderRow.getCell(colIndex).font = { bold: true }
            colIndex++
          })
        }
      })

      const summaryHeaders = [
        'Total Absent',
        'Total Present',
        'Total Week-off',
        'Total Miss punch',
        'Total Leaves',
        'Clock In (in %)'
      ]
      summaryHeaders.forEach((header) => {
        const cell = mainHeaderRow.getCell(colIndex)
        cell.value = header
        cell.font = { bold: true }
        worksheet.mergeCells(1, colIndex, 2, colIndex)
        colIndex++
      })

      mainHeaderRow.height = 30
      subHeaderRow.height = 25

      sortedItems.forEach((item) => {
        const chain = reporteeChain[item.orgEmployeeId || item.employeeId]?.managerChain || ''
        const list = chain ? chain.split(' -> ') : []
        const managerCols = Array.from({ length: maxManagers }, (_, i) => list[i] || '-')

        const row = [
          item.name,
          item.orgEmployeeId || item.employeeId,
          item.role,
          item.designation,
          item.city,
          ...managerCols,
          chain
        ]

        let totalAbsent = 0
        let totalPresent = 0
        let totalWeekOff = 0
        let totalMissPunch = 0
        let totalLeaves = 0
        let daysCount = 0

        currentMonthDates.forEach((date) => {
          daysCount++
          const att = item.attendance && item.attendance[date] ? item.attendance[date] : {}

          const dateCols = selectedColumns.filter((c) => ATTENDANCE_VALID_COLS.includes(c))

          dateCols.forEach((col) => {
            const mapper = ATTENDANCE_COLUMN_MAPPERS[col]
            const val = mapper ? mapper(att, date) : att[col] || '-'
            row.push(val)

            if (col === 'Attendance') {
              if (val === 'ABSENT' || val === 'A') totalAbsent++
              if (val === 'PRESENT' || val === 'P') totalPresent++
              if (val === 'WEEK_OFF' || val === 'WO') totalWeekOff++
              if (val === 'LEAVE' || val === 'L') totalLeaves++
              if (val === 'MISS_PUNCH' || val === 'MP') totalMissPunch++
            }
          })
        })

        const clockInPercentage =
          daysCount > 0 ? `${((totalPresent * 100) / daysCount).toFixed(2)} %` : '0.00 %'
        row.push(
          totalAbsent,
          totalPresent,
          totalWeekOff,
          totalMissPunch,
          totalLeaves,
          clockInPercentage
        )
        worksheet.addRow(row)
      })

      const buffer = await workbook.xlsx.writeBuffer()
      this.downloadBlob(buffer, `Attendance_${start}_${end}.xlsx`)
      this.$toast?.success('Report generated successfully!')
    },

    async generateMusterReportI(items, currentMonthDates, selectedColumns) {
      const workbook = new ExcelJS.Workbook()
      const worksheet = workbook.addWorksheet('Muster Report-I')
      const sortedItems = sortDropdownOptions([...items], 'name')
      const start = this.dates.sort()[0]
      const end = this.dates.sort()[1]

      const reporteeChain = this.$storage?.getUniversal('userReporteeChain') || {}
      let maxManagers = 0
      sortedItems.forEach((employee) => {
        const chain =
          reporteeChain[employee.orgEmployeeId || employee.employeeId]?.managerChain || ''
        const list = chain ? chain.split(' -> ') : []
        maxManagers = Math.max(maxManagers, list.length)
      })
      const managerHeaders = Array.from({ length: maxManagers }, (_, i) => `Manager Level ${i + 1}`)

      const headers = [
        'Sr.No',
        'Employee Name',
        'Employee Id',
        'Role',
        'Designation',
        'City',
        ...managerHeaders,
        'Manager Hierarchy',
        'Date',
        ...selectedColumns,
        'Monthly Distance (km)'
      ]

      const headerRow = worksheet.getRow(1)
      headerRow.values = headers
      headerRow.font = { bold: true }

      let srNo = 1
      sortedItems.forEach((employee) => {
        let totalDistance = 0
        const chain =
          reporteeChain[employee.orgEmployeeId || employee.employeeId]?.managerChain || ''
        const list = chain ? chain.split(' -> ') : []
        const managerCols = Array.from({ length: maxManagers }, (_, i) => list[i] || '-')

        currentMonthDates.forEach((date, dateIdx) => {
          const att =
            employee.attendance && employee.attendance[date] ? employee.attendance[date] : {}
          if (att['Distance (km)'] && !isNaN(parseFloat(att['Distance (km)']))) {
            totalDistance += parseFloat(att['Distance (km)'])
          }

          const row = [
            dateIdx === 0 ? srNo : '',
            dateIdx === 0 ? employee.name : '',
            dateIdx === 0 ? employee.orgEmployeeId || employee.employeeId : '',
            dateIdx === 0 ? employee.role : '',
            dateIdx === 0 ? employee.designation : '',
            dateIdx === 0 ? employee.city : '',
            ...(dateIdx === 0 ? managerCols : Array(maxManagers).fill('')),
            dateIdx === 0 ? chain : '',
            dayjs(date).format('DD/MM/YYYY'),
            ...selectedColumns.map((col) => {
              const mapper = ATTENDANCE_COLUMN_MAPPERS[col]
              return mapper ? mapper(att, date) : att[col] || '-'
            }),
            dateIdx === 0 ? '---' : '' // Placeholder for Monthly Distance
          ]
          const addedRow = worksheet.addRow(row)
          // Update total distance in the first row after processing all dates
          if (dateIdx === currentMonthDates.length - 1) {
            const firstRowIdx = addedRow.number - currentMonthDates.length + 1
            worksheet.getRow(firstRowIdx).getCell(headers.length).value = totalDistance.toFixed(2)
          }
        })
        srNo++
      })

      const buffer = await workbook.xlsx.writeBuffer()
      this.downloadBlob(buffer, `Muster_Report_I_${start}_${end}.xlsx`)
      this.$toast?.success('Report generated successfully!')
    },

    getTimeSeriesHeaders(reportTitle, selectedColumns) {
      const reporteeChain = this.$storage?.getUniversal('userReporteeChain') || {}
      let maxManagers = localStorage.getItem('maxManagerCount') || 3 // rough fallback
      const managerHeaders = Array.from({ length: maxManagers }, (_, i) => `Manager Level ${i + 1}`)

      const baseHeaders = [
        'Sr.No',
        'Employee Name',
        'Employee Id',
        'Role',
        'Designation',
        'City',
        ...managerHeaders,
        'Manager Hierarchy'
      ]
      return [...baseHeaders, ...selectedColumns]
    },

    processDailyPoints(points, statusFromApi) {
      if (!points || points.length === 0) {
        return {
          attendanceStatus: statusFromApi || '-',
          firstClockIn: '-',
          lastClockOut: '-',
          dailyDistance: 0,
          workingHours: '-',
          breaksCount: 0,
          dailyVisitCount: 0,
          pairs: []
        }
      }

      points.sort(
        (a, b) => new Date(a.timestamp || a.createdAt) - new Date(b.timestamp || b.createdAt)
      )

      const firstPoint = points[0]
      const lastPoint = points.length > 1 ? points[points.length - 1] : firstPoint
      const firstTime = dayjs(firstPoint.timestamp || firstPoint.createdAt)
      const lastTime = dayjs(lastPoint.timestamp || lastPoint.createdAt)

      const totalDist = this.calculateTotalDistance(points)
      const stops = this.groupPointsIntoStops(points)
      const pairs = []

      if (stops.length > 0) {
        for (let i = 0; i < stops.length; i++) {
          const sCurr = stops[i]
          const sPrev = i > 0 ? stops[i - 1] : null
          const sNext = i < stops.length - 1 ? stops[i + 1] : null

          let distFromPrev = 0
          if (sPrev)
            distFromPrev = this.calculateDistance(sPrev.lat, sPrev.lng, sCurr.lat, sCurr.lng)

          const durationMs = dayjs(sCurr.endTime).diff(dayjs(sCurr.startTime))

          pairs.push({
            origin: sCurr.address,
            destination: sNext ? sNext.address : '-',
            originAction: sCurr.action,
            segmentDistance: distFromPrev,
            customerName: sCurr.customerName || '-',
            originArrivalTime: sCurr.startTime,
            originDepartureTime: sCurr.endTime,
            originDuration: this.formatDuration(durationMs)
          })
        }
      } else {
        const distFirstLast = this.calculateDistance(
          firstPoint.lat || firstPoint.location?.coordinates?.[1] || 0,
          firstPoint.lng || firstPoint.location?.coordinates?.[0] || 0,
          lastPoint.lat || lastPoint.location?.coordinates?.[1] || 0,
          lastPoint.lng || lastPoint.location?.coordinates?.[0] || 0
        )
        if (distFirstLast > 0.1) {
          pairs.push({
            origin: firstPoint.address || firstPoint.location?.address || '-',
            destination: lastPoint.address || lastPoint.location?.address || '-',
            originAction: '-',
            segmentDistance: 0,
            customerName: '-',
            originArrivalTime: firstPoint.timestamp || firstPoint.createdAt,
            originDepartureTime: null,
            originDuration: '-'
          })
          pairs.push({
            origin: lastPoint.address || lastPoint.location?.address || '-',
            destination: '-',
            originAction: '-',
            segmentDistance: distFirstLast,
            customerName: '-',
            originArrivalTime: lastPoint.timestamp || lastPoint.createdAt,
            originDepartureTime: null,
            originDuration: '-'
          })
        } else {
          pairs.push({
            origin: firstPoint.address || firstPoint.location?.address || '-',
            destination: '-',
            originAction: '-',
            segmentDistance: totalDist,
            customerName: '-',
            originArrivalTime: null,
            originDepartureTime: null,
            originDuration: '-'
          })
        }
      }

      const workingHours = lastTime.diff(firstTime, 'hour', true)

      return {
        attendanceStatus: statusFromApi || '-',
        firstClockIn: firstTime.format('hh:mm A'),
        lastClockOut: points.length > 1 ? lastTime.format('hh:mm A') : '-',
        dailyDistance: totalDist,
        workingHours: workingHours > 0 ? workingHours.toFixed(2) + ' hrs' : '-',
        breaksCount: 0, // Placeholder
        dailyVisitCount: this.calculateDailyUniqueVisits(points),
        pairs: pairs
      }
    },

    groupPointsIntoStops(points) {
      const groups = []
      let currentGroup = null

      points.forEach((p) => {
        const state = p.state || 'UNKNOWN'
        const isStill = state === 'STILL'
        const pAddress = (p.address || p.location?.address || '').trim()
        const pPrefix = pAddress.split(',')[0].trim()
        const pTime = p.timestamp || p.createdAt

        let lat, lng
        if (p.location?.coordinates) {
          lng = p.location.coordinates[0]
          lat = p.location.coordinates[1]
        } else {
          lat = p.lat
          lng = p.lng
        }
        if (!lat || !lng) return

        if (isStill) {
          if (currentGroup && currentGroup.addressPrefix === pPrefix) {
            currentGroup.endTime = pTime
          } else {
            if (currentGroup) groups.push(currentGroup)
            currentGroup = {
              address: pAddress,
              addressPrefix: pPrefix,
              lat,
              lng,
              startTime: pTime,
              endTime: pTime,
              action: p.activity?.action || '-',
              customerName: p.activity?.caseTaskDetails?.customerName || '-'
            }
          }
        } else if (currentGroup) {
          groups.push(currentGroup)
          currentGroup = null
        }
      })
      if (currentGroup) groups.push(currentGroup)
      return groups
    },

    calculateDistance(lat1, lon1, lat2, lon2) {
      if (!lat1 || !lon1 || !lat2 || !lon2) return 0
      const R = 6371
      const dLat = this.deg2rad(lat2 - lat1)
      const dLon = this.deg2rad(lon2 - lon1)
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(this.deg2rad(lat1)) *
          Math.cos(this.deg2rad(lat2)) *
          Math.sin(dLon / 2) *
          Math.sin(dLon / 2)
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
      return R * c
    },

    deg2rad(deg) {
      return deg * (Math.PI / 180)
    },

    formatDuration(ms) {
      if (!ms || ms < 0) ms = 0
      const seconds = Math.floor(ms / 1000)
      const h = Math.floor(seconds / 3600)
      const m = Math.floor((seconds % 3600) / 60)
      const s = seconds % 60
      if (h > 0) return `${h}h ${m}m ${s}s`
      if (m > 0) return `${m}m ${s}s`
      return `${s}s`
    },

    calculateTotalDistance(points) {
      let total = 0
      for (let i = 0; i < points.length - 1; i++) {
        let lat1, lng1, lat2, lng2
        const p1 = points[i],
          p2 = points[i + 1]
        if (p1.location?.coordinates) {
          lng1 = p1.location.coordinates[0]
          lat1 = p1.location.coordinates[1]
        } else {
          lat1 = p1.lat
          lng1 = p1.lng
        }
        if (p2.location?.coordinates) {
          lng2 = p2.location.coordinates[0]
          lat2 = p2.location.coordinates[1]
        } else {
          lat2 = p2.lat
          lng2 = p2.lng
        }
        if (lat1 && lng1 && lat2 && lng2) total += this.calculateDistance(lat1, lng1, lat2, lng2)
      }
      return total
    },

    calculateDailyUniqueVisits(points) {
      if (!points) return 0
      const unique = new Set()
      points.forEach((p) => {
        if (p.activity?.caseTaskDetails)
          unique.add(p.activity.caseTaskDetails._id || p.activity.caseTaskDetails)
      })
      return unique.size
    },

    downloadBlob(buffer, fileName) {
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }
}
</script>

<style scoped>
.v-card {
  border: 1px solid #b3b8c0;
}
.v-btn {
  text-transform: none;
  font-weight: 500;
}

.v-dialog > .v-card > .v-card__text {
  padding: 0;
}

/* Premium Filter Dialog Styling */
.report-gen-card {
  border-radius: 20px !important;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
}

.date-picker-trigger {
  min-height: 56px;
  background-color: #fafafa;
}

.report-card-container {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.report-card-container:hover {
  transform: translateY(-8px);
}

.report-card-container:hover .v-card {
  box-shadow: 0 15px 30px -5px rgba(0, 0, 0, 0.1) !important;
  border-color: #1a73e8 !important;
}

/* Custom checkbox styling */
.v-input--checkbox {
  margin-top: 0;
  padding-top: 0;
}

.v-input--checkbox .v-input__slot {
  margin-bottom: 0;
}
.form-title {
  font-size: 1.25rem;
  padding: 0px !important;
  font-weight: 600;
  color: #21427d;
  margin: 0;
}
.table-radius {
  border-radius: 0px !important;
}
.font-title {
  font-size: 0.875rem;
  color: #000;
  margin-bottom: 0px !important;
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Report Preview Table Styling */
.report-preview-table ::v-deep th {
  padding: 16px 24px !important;
  font-weight: 700 !important;
  color: #1f2937 !important;
  background-color: #f8fafc !important;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 0.75rem;
  white-space: nowrap;
}

.report-preview-table ::v-deep td {
  padding: 16px 24px !important;
  color: #4b5563 !important;
  font-size: 0.875rem;
  border-bottom: 1px solid #f1f5f9 !important;
  white-space: nowrap;
}

.report-preview-table ::v-deep tr:hover {
  background-color: #f1f5f9 !important;
}
</style>
